import React from 'react';
import { Text, mergeClasses } from '@fluentui/react-components';
import { Box } from '../../../../theme/fluent';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppLayoutStyles } from '../../AppLayout.styles';
import { NavigationItem } from '../../app-layout-components/navigation-item';
import { NavigationItemsProps } from './NavigationItems.types';

export const NavigationItems: React.FC<NavigationItemsProps> = ({
  dashboardItem,
  customShortcuts,
  otherNavigationItems,
  isCollapsed,
}) => {
  const { classes } = useAppLayoutStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPanel = new URLSearchParams(location.search).get('panel');
  const containerRef = React.useRef<HTMLElement>(null);

  // After navigating, put focus on the skip link so keyboard users can jump straight to the new page's content
  const navigateAndFocusSkipLink = (path: string) => {
    navigate(path);
    document.getElementById('skip-to-main-content')?.focus();
  };

  // Arrow Up/Down moves focus between nav items instead of relying on Tab alone
  const handleContainerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[role="button"]') ?? []);
    if (items.length === 0) return;

    event.preventDefault();
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + delta + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return (
    <Box
      ref={containerRef}
      py="s"
      className={mergeClasses(classes.navItem, 'scrollable-content')}
      onKeyDown={handleContainerKeyDown}
    >
      {/* Dashboard - always first */}
      {dashboardItem && (
        <NavigationItem
          item={dashboardItem}
          isActive={location.pathname === dashboardItem.path && !currentPanel}
          isCollapsed={isCollapsed}
          onClick={() => navigateAndFocusSkipLink(dashboardItem.path)}
        />
      )}

      {/* Separator between Dashboard and Tools - only shown when collapsed */}
      {dashboardItem && customShortcuts.length > 0 && isCollapsed && <Box className={classes.navItemCollapsedLine} />}

      {/* Tools Section Header - only shown when expanded */}
      {!isCollapsed && customShortcuts.length > 0 && (
        <Box px="l" py="xs" className={classes.toolHeader}>
          <Text size={200} weight="semibold" className={classes.toolHeaderText}>
            Tools
          </Text>
        </Box>
      )}

      {/* Custom shortcuts */}
      {customShortcuts.map((item) => {
        const isActive =
          location.pathname === item.path &&
          (currentPanel === item.panelId || item.activeOn?.includes(currentPanel ?? ''));

        return (
          <NavigationItem
            key={item.text}
            item={item}
            isActive={isActive || false}
            isCollapsed={isCollapsed}
            onClick={() => navigateAndFocusSkipLink(`${item.path}?panel=${item.panelId}`)}
          />
        );
      })}

      {/* Other pages (excluding Dashboard) */}
      {otherNavigationItems.map((item) => (
        <NavigationItem
          key={item.text}
          item={item}
          isActive={location.pathname === item.path}
          isCollapsed={isCollapsed}
          onClick={() => navigateAndFocusSkipLink(item.path)}
        />
      ))}
    </Box>
  );
};
